import React, { useState, useContext, useRef, useEffect } from 'react';
import DataContext from 'UserRegistration/contexts/DataContext';
import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { pushCheckboxState } from 'UserRegistration/resources/utils/analytics';
import { formatPrice } from '../../../commons/resources/utils/numeric';
import { updateBillingRequired } from 'UserRegistration/contexts/configs/componentConfig';
import {calculateTotal} from '../../resources/utils/packageSelectionUtils';
import adImg from '../../../commons/resources/images/AdvertisingProfile.svg';

interface ValueAddedServiceProps {
    isSelectedByDefault? : boolean,
    totalPrices: any,
    vasItemId : number
}

function AdvertisingVas(props : ValueAddedServiceProps) {

    const [isSelected, setIsSelected] = useState(props.isSelectedByDefault);
    const { registrationData, setRegistrationData } = useContext(DataContext);
    const { componentConfig, setComponentConfig } = useContext(ConfigurationContext);
    const { t, i18n }:any = useTranslation(componentConfig.i18nNamespaces);
    const price = props.totalPrices[registrationData.registrationModel.subscription.selectedFrequency];
    const registrationDataRef = useRef(registrationData);

    // A-B Testing for EAP link between pakcage selection and automatic selection for EAP
    if(componentConfig.settings.abTestSettings.eaplinkABTestSetting=="TEST_A") {
        registrationData.eapLinkDisabled = true;
    }

    const onClickHandler = () => {

        const localIsSelected = !isSelected;
        
        setIsSelected(localIsSelected);
        
        const updatedRegistrationData = Object.assign({}, registrationData);

        // adding the vas item to the subscription
        if (localIsSelected && !updatedRegistrationData.registrationModel.subscription.vasItemIds.includes(props.vasItemId)) {
            updatedRegistrationData.registrationModel.subscription.vasItemIds.push(props.vasItemId);

            // if this vasItem is in the removed vases list, filter it out
            updatedRegistrationData.registrationModel.subscription.vasRemovedIds ??= [];
            updatedRegistrationData.registrationModel.subscription.vasRemovedIds = 
                        updatedRegistrationData.registrationModel.subscription.vasRemovedIds.filter( (id: number) => { id !== props.vasItemId } );

        // remove the vas item from the subscription
        } else if (!localIsSelected && updatedRegistrationData.registrationModel.subscription.vasItemIds.includes(props.vasItemId)) {
            updatedRegistrationData.registrationModel.subscription.vasItemIds = updatedRegistrationData.registrationModel.subscription.vasItemIds.filter(
                (vasItemId: number) => { vasItemId != props.vasItemId }
            );
            
            // and add it to the list of removed vases --if the list is null or undefined, initialize it and then push its ID
            updatedRegistrationData.registrationModel.subscription.vasRemovedIds ??= [];
            updatedRegistrationData.registrationModel.subscription.vasRemovedIds.push(props.vasItemId);
        }

        updateBillingRequired(updatedRegistrationData, componentConfig, setComponentConfig);

        pushCheckboxState("AdvertisingVasSelector", localIsSelected ? "On" : "Off");
        setRegistrationData(updatedRegistrationData);
    };

    /**
     * METS-6302 : EAP Preselected for Paying Options A/B Testing
     * registrationData.eapLinkDisabled: FALSE by default, disables this functionality between Package Selection and EAP when TRUE
     */
    useEffect(() => {
        if(registrationData.registrationModel.selectedSubscriptionPackage!=null && !registrationData.eapLinkDisabled) {
            if((registrationData.registrationModel.selectedSubscriptionPackage.freePackage==false && isSelected?.valueOf()==false) || 
              (registrationData.registrationModel.selectedSubscriptionPackage.freePackage==true && isSelected?.valueOf()==true)) {
                onClickHandler();  
            }
        }
    }, [
        registrationData.registrationModel.subscription.selectedGroup.groupId,
        JSON.stringify(registrationData.registrationModel.selectedSecondarySubscriptions),
        JSON.stringify(registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds)
        ]);

    /**
     * METS-853 : EAP not being added to Order Details
     */

    useEffect(() => {
        registrationDataRef.current = registrationData;
        calculateTotal(registrationDataRef.current, setRegistrationData);
    }, [isSelected]);

    // update isSelected using the list of removed vas id's so the toggle switch can keep up with the sidebar
    useEffect(() => {
        if (isSelected && registrationData.registrationModel.subscription.vasRemovedIds && registrationData.registrationModel.subscription.vasRemovedIds.includes(props.vasItemId)) {
            setIsSelected(!isSelected);

            // Disable link between EAP and package selection when EAP manually removed from sidebar
            registrationData.eapLinkDisabled = true;
        }
    }, [JSON.stringify(registrationData.registrationModel.subscription.vasRemovedIds)]);

    const renderVasDetails = () => {

        const details = t("vas.advertisingVas.details").split("|");
        const listItems = details.map((item: string, index: number) => {
            return <li key={index}>{item}</li>
        });

        return (
            <div>
                <p className="title">{t("vas.advertisingVas.title")}</p>
                <ul>
                    {listItems}
                </ul>
            </div>
        );

    };

    return (
        <div className={"advertising-vas-container"}>
            <p className="advertising-vas-title">{t("vas.advertisingVas.header")}</p>
            
            <div className={classnames("vas-container", "advertising-vas", { "selected": isSelected })}>
                <div className="columns-wrapper">
                    <div className="left-section">
                        <div className="description-container">
                            <p className="tagline">{t("vas.advertisingVas.image.details")}</p>
                        </div>
                        <div className="image-container">
                            <img src={adImg} className="ad-image"/>
                        </div>
                    </div>
                    <div className="right-section">
                        <div className="vas-description">
                            {renderVasDetails()}
                        </div>
                        <div className="button-box">
                                {/* Button for Selection */}
                                    <button type="button" className={`select-btn ${isSelected ? "selected" : ""}`}  onClick={() => {
                                                                                                                    registrationData.eapLinkDisabled = true;
                                                                                                                    onClickHandler(); }}>
                                    {isSelected 
                                        ? !isNaN(price)  ? `${t("vas.advertisingVas.remove.from.cart")} (-${formatPrice(price, i18n.language, componentConfig.currencyCode)})` 
                                            : `${t("vas.advertisingVas.remove.from.cart")}` 
                                            : !isNaN(price)  ? `${t("vas.advertisingVas.add.to.cart")} (+${formatPrice(price, i18n.language, componentConfig.currencyCode)})`
                                            : `${t("vas.advertisingVas.add.to.cart")}`}
                                    </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdvertisingVas;